import * as React from 'react';
import classnames from 'classnames';
import { FullProduct } from '@msdyn365-commerce/commerce-entities';
import { ICartState } from '@msdyn365-commerce/global-state';
import { ICoreContext, IImageSettings } from '@msdyn365-commerce/core';
import { AttributeValue, CartLine, CategoryPath, CategoryPathSegment, CommerceList, Customer, ProductList, ProductPrice, SimpleProduct, UnitOfMeasureConversion, PeriodicDiscountOfferType } from '@msdyn365-commerce/retail-proxy';
import {
    ArrayExtensions,
    IProductInventoryInformation,
    OrderTemplate, getFallbackImageUrl
} from '@msdyn365-commerce-modules/retail-actions';
import { ICartlinesViewProps } from '@msdyn365-commerce-modules/cart';
import { ProductListRow } from './product-list-row';
import { useEffect, useMemo, useState } from 'react';
import PQueue from 'p-queue';
import { ValidateProductAccessResult } from '../../shared-utils/validate-product-access';
import { IProductError, SimpleProductExt, ProductSearchResultExt } from '../../shared-utils/types';
import { CountingListLine } from '../../actions/entities/counting-list';
import { getDiscountBadge } from '../../shared-utils/badge-utils';
import { ICartViewProps } from '../../modules/vinhuset-cart/vinhuset-cart';
import ProductListRowShimmer from './product-list-row-shimmer';

// Modules where the ProductListComponent is used:
// - Cart
// - Search Result Container
// - Wishlist
// - Order Template

export class ProductListProduct {
    public RecordId?: number;
    public ItemId?: string;
    public Name?: string;
    public PrimaryImageUrl?: string;
    public DefaultUnitOfMeasure?: string;
    public Price?: number;
    public BasePrice?: number;
    public InventoryLabel?: string | undefined;
    public CurrentQuantity?: number | undefined;
    public CartLine?: CartLine | undefined;
    public DiscountAmount?: number | undefined;
    public LineAmount?: number | undefined;
    public Availability?: IProductInventoryInformation;
    public AlcoholPercentage?: string | undefined;
    public VolumeCl?: string | undefined;
    public PriceFailed?: boolean | undefined;
    public LineCreatedDateTime?: Date | undefined;
    public DisplayOrder?: number;
    public CategoryPaths?: CategoryPath[] | undefined;
    public Badge?: string | undefined;
    public ProductPrice?: ProductPrice | undefined;
    public RequestedDeliveryDate?: Date | undefined;
    public Producer?: string | undefined;
    public CountryAndDistrict?: string | undefined;
    public Category?: CategoryPathSegment | undefined;
    public Vintage?: string | undefined;
    public SoldOutUntilDate?: Date;
    public SoldOutUnknownDate?: boolean | undefined;
    public LineId?: string | undefined;
    public FreeItemQuantity?: number | undefined;

    public constructor(
        recordId?: number,
        itemId?: string,
        name?: string,
        primaryImageUrl?: string,
        defaultUnitOfMeasure?: string,
        price?: number,
        inventoryLabel?: string | undefined,
        currentQuantity?: number | undefined,
        cartLine?: CartLine | undefined,
        DiscountAmount?: number | undefined,
        LineAmount?: number | undefined,
        availability?: IProductInventoryInformation,
        alcoholPercentage?: string | undefined,
        volumeCl?: string | undefined,
        priceFailed?: boolean | undefined,
        displayOrder?: number | undefined,
        categoryPaths?: CategoryPath[] | undefined,
        badge?: string | undefined,
        productPrice?: ProductPrice | undefined,
        producer?: string | undefined,
        countryAndDistrict?: string | undefined,
        category?: CategoryPathSegment | undefined,
        vintage?: string | undefined,
        soldOutUntilDate?: Date | undefined,
        soldOutUnknownDate?: boolean | undefined,
        lineId?: string,
        freeItemQuantity?: number
    ) {
        this.RecordId = recordId;
        this.ItemId = itemId;
        this.Name = name;
        this.PrimaryImageUrl = primaryImageUrl;
        this.DefaultUnitOfMeasure = defaultUnitOfMeasure;
        this.Price = price;
        this.InventoryLabel = inventoryLabel;
        this.CurrentQuantity = currentQuantity;
        this.CartLine = cartLine;
        this.DiscountAmount = DiscountAmount;
        this.LineAmount = LineAmount;
        this.Availability = availability;
        this.AlcoholPercentage = alcoholPercentage;
        this.VolumeCl = volumeCl;
        this.PriceFailed = priceFailed;
        this.DisplayOrder = displayOrder || 0;
        this.CategoryPaths = categoryPaths;
        this.Badge = badge;
        this.ProductPrice = productPrice;
        this.Producer = producer;
        this.CountryAndDistrict = countryAndDistrict;
        this.Category = category;
        this.Vintage = vintage;
        this.SoldOutUntilDate = soldOutUntilDate;
        this.SoldOutUnknownDate = soldOutUnknownDate;
        this.LineId = lineId;
        this.FreeItemQuantity = freeItemQuantity;
    }
}

export interface IProductListResources {
    buyNowButtonText: string;
    removeButtonText?: string;
    priceErrorText?: string;
    inCartTooltipText?: string;
    deliveryDateLabel?: string;
    addToWishlistText?: string;
    removeFromWishlistText?: string;
    depositSuffixText?: string;
    pricePerLiterText?: string;
    pricePerText?: string;
    expectedInventoryDateWithQuantityText?: string;
    expectedInventoryDateText?: string;
    soldOutUntilDateText?: string;
    soldOutUnknownDateText?: string;
}

export interface IActionsAvailable {
    addToCartAction?: boolean;
    addToCartOrderTemplate?: boolean;
    addToOrderTemplate?: boolean;
    addToWishlistAction?: boolean;
    removeAction?: boolean;
    productComparisonButton?: boolean;
}

interface IProductListItems {
    type?: 'default' | 'cart';
    cart?: ICartState;
    products: ProductListProduct[];
    simpleProducts: SimpleProduct[];
    unitConversions?: UnitOfMeasureConversion[];
    orderTemplates?: OrderTemplate[];
    productErrors?: IProductError[];
    context: ICoreContext;
    resources: IProductListResources;
    imageSettings?: IImageSettings;
    moduleType: string;
    moduleId: string;
    allowMultiSelect?: boolean;
    orderTemplate?: OrderTemplate;
    customerInformation?: Customer;
    productComparisonButton?: {} | null | undefined;
    isLoading?: boolean;
    alwaysBreakoff?: boolean;
    disableBuyButtons?: boolean;
    disableReorder?: boolean;
    disableQuantityInput?: boolean;
    draggable?: boolean;
    historicalPrice?: boolean;
    min?: number;
    showAvailability?: boolean;
    showPrices?: boolean;
    showTotalPrices?: boolean;
    showCartCount?: boolean;
    showGroups?: boolean;
    readOnly?: boolean;
    buyAlwaysAdd?: boolean;
    resetSelectedQuantities?: Set<number>;
    smallerImage?: boolean;
    onResetSelectedQuantities?: () => void;
    validatedProductAccess?: ValidateProductAccessResult[];
    actionsAvailable?: IActionsAvailable;
    cartViewProps?: ICartViewProps | undefined;
    selectOnQuantityChange?: boolean;
    breakoffProducts?: { [key: number]: boolean };
    selectedProducts?: IProductSelection;
    wishlists?: CommerceList[];
    onProductRemove?: (product: ProductListProduct) => Promise<void>;
    onUpdateOrderTemplates?: (orderTemplate?: ProductList) => Promise<OrderTemplate[]>;
    onUpdateQuantity?: (product: ProductListProduct, quantity: number) => void;
    onUpdateSelection?: (selection: IProductSelection) => void;
    onBreakoffChange?: (product: ProductListProduct, enabled: boolean) => void;
    onDrop?: (productRecId: number | undefined, targetRecId: number | undefined, direction: 'over' | 'under') => void;
    onBuyValidate?: (product: ProductListProduct, quantity: number) => Promise<BuyValidationResult>;
    retrieveOrderTemplates?(): Promise<OrderTemplate[]>;
}

export interface IProductSelection {
    [key: string]: boolean;
}

export interface BuyValidationResult {
    isValid: boolean;
    message?: string;
}

export const ProductListComponent: React.FC<IProductListItems> = ({
    type = 'default',
    cart,
    products,
    simpleProducts,
    unitConversions,
    orderTemplates,
    productErrors,
    orderTemplate,
    customerInformation,
    context,
    imageSettings,
    resources,
    moduleType,
    moduleId,
    allowMultiSelect,
    productComparisonButton,
    isLoading,
    readOnly,
    alwaysBreakoff,
    disableBuyButtons,
    disableReorder,
    disableQuantityInput,
    draggable,
    historicalPrice,
    min,
    showAvailability,
    showPrices,
    showTotalPrices,
    showCartCount,
    showGroups,
    buyAlwaysAdd,
    resetSelectedQuantities,
    smallerImage,
    onResetSelectedQuantities,
    validatedProductAccess,
    actionsAvailable,
    cartViewProps,
    selectOnQuantityChange,
    breakoffProducts,
    selectedProducts: selectedProductsProp,
    wishlists,
    onProductRemove,
    onUpdateOrderTemplates,
    onUpdateQuantity,
    onUpdateSelection,
    onBreakoffChange,
    onDrop,
    onBuyValidate,
    retrieveOrderTemplates
}) => {
    const [selectedProducts, setSelectedProducts] = useState<IProductSelection>(selectedProductsProp || {});
    const [dragOverProduct, setDragOverProduct] = useState<number | undefined>(undefined);
    const [dragOverUnder, setDragOverUnder] = useState<'over' | 'under' | undefined>(undefined);
    const cartQueue = useMemo(() => new PQueue({ concurrency: 1 }), []);

    useEffect(() => {
        setSelectedProducts(selectedProductsProp || {});
    }, [selectedProductsProp]);

    useEffect(() => {
        onUpdateSelection?.(selectedProducts);
    }, [selectedProducts]);

    React.useEffect(() => {
        document.querySelectorAll('.msc-add-to-order-template-icon').forEach(element => {
            element.setAttribute('title', element.getAttribute('aria-label') || '');
        });

        // Remove selection from products that are no longer available (for example on another page)
        Object.keys(selectedProducts).forEach(key => {
            const id = Number(key);

            if (isNaN(id)) {
                return;
            }

            if (products.every(x => x.RecordId !== id)) {
                delete selectedProducts[key];
            }
        });
    }, [products]);

    // Setup navigation with Enter and Shift+Enter for quantity inputs
    React.useEffect(() => {
        const inputs = document.querySelectorAll('.vinhuset-quantity__quantity-input');
        const keyDownHandler = (e: Event) => {
            const event = e as KeyboardEvent;
            const index = Number((event.currentTarget as HTMLElement).getAttribute('data-index'));

            if (event.key === 'Enter') {
                if (event.shiftKey) {
                    if (index === 0) {
                        return;
                    }
                    (inputs.item(index - 1) as HTMLInputElement).select();
                } else {
                    if (index === inputs.length - 1) {
                        return;
                    }
                    (inputs.item(index + 1) as HTMLInputElement).select();
                }
            }
        };

        inputs.forEach((element, index) => {
            element.setAttribute('data-index', index.toString());
            element.addEventListener('keydown', keyDownHandler);
        });

        return () => {
            inputs.forEach(element => {
                element.removeEventListener('keydown', keyDownHandler);
            });
        };
    }, [products]);

    React.useEffect(() => {
        if (resetSelectedQuantities) {
            resetSelectedQuantities.forEach(id => {
                if (products.find(x => x.RecordId === id)) {
                    products.find(x => x.RecordId === id)!.CurrentQuantity = 0;
                }
            });

            onResetSelectedQuantities?.();
        }
    }, [resetSelectedQuantities]);

    const changeSelection = (id: number | undefined) => {
        if (id) {
            setSelectedProducts((prevState) => {
                prevState[id] = !prevState[id];
                return { ...prevState };
            });
        }
    };
    const handleDrop = async (e: React.DragEvent<HTMLElement>, recordId: number | undefined, overUnder: "over" | "under" | undefined) => {
        const dragData = JSON.parse(e.dataTransfer.getData('product'));
        const dragProduct = products.find(product => product.RecordId === dragData.ProductRecordId);
        const targetProduct = products.find(product => product.RecordId === recordId);

        if (!dragProduct || !targetProduct || !overUnder) {
            return;
        }

        onDrop?.(dragProduct?.RecordId, targetProduct?.RecordId, overUnder);
    };

    const grouped = products.reduce((acc, product) => {
        const key = showGroups && product.RequestedDeliveryDate ? new Date(product.RequestedDeliveryDate).getTime().toString() : 'default';
        if (!acc[key]) {
            acc[key] = [];
        }
        acc[key].push(product);
        return acc;
    }, {} as { [key: string]: ProductListProduct[] });
    const createdGroupFor: string[] = [];

    return (
        <div>
            <ul
                className={classnames('product-list-component list product-listing')}
                onDrop={async e => {
                    await handleDrop(e, dragOverProduct, 'under');
                }}
                onDragOver={e => {
                    e.stopPropagation();

                    if (!disableReorder && e.dataTransfer.types.includes('product')) {
                        e.preventDefault();

                        const firstItem = document.querySelector('.product-list-component > li:first-of-type');
                        const margin = firstItem ? parseFloat(window.getComputedStyle(firstItem).marginBottom) + 6 : 22;
                        const item = document.elementFromPoint(e.clientX, e.clientY - margin);

                        let listItem = item?.parentElement;
                        while (listItem) {
                            if (listItem.tagName === 'LI') {
                                break;
                            }
                            listItem = listItem.parentElement;
                        }

                        if (!listItem) {
                            return;
                        }

                        const productId = listItem?.getAttribute('data-product-id') ? parseFloat(listItem.getAttribute('data-product-id')!) : undefined;

                        setDragOverProduct(productId);
                        setDragOverUnder('under');
                    }
                }}
            >
                {Object.keys(grouped).map((key, index) => {
                    const items: React.ReactNode[] = [];

                    if (key !== 'default' && !createdGroupFor.includes(key)) {
                        items.push(
                            <li key={`${key}-group`} className='product-list-group-header'>
                                {resources.deliveryDateLabel}: {context.cultureFormatter.formatDate(new Date(parseFloat(key)), { day: 'numeric', month: 'long' })}
                            </li>
                        );
                        createdGroupFor.push(key);
                    }

                    for (const product of grouped[key]) {
                        if (product.Name === 'Loading...') {
                            items.push(
                                <ProductListRowShimmer
                                    className='product-search-table-row'
                                    key={`${product.RecordId}-${product.DefaultUnitOfMeasure}-${product.CartLine?.LineId}`}
                                />
                            );
                            continue;
                        }

                        items.push((
                            <ProductListRow
                                key={`${product.RecordId}-${product.DefaultUnitOfMeasure}`}
                                context={context}
                                typeName='product-search-table-row'
                                id={`product-search-table-row-${index}`}
                                className='product-search-table-row'
                                resources={resources}
                                cartQueue={cartQueue}
                                selectOnQuantityChange={selectOnQuantityChange}
                                isDragOver={!disableReorder && product.RecordId === dragOverProduct ? dragOverUnder : undefined}
                                isSelected={product.RecordId ? selectedProducts[product.RecordId] : false}
                                onSelectCartHandler={() => changeSelection(product.RecordId)}
                                data={{
                                    cart,
                                    product,
                                    simpleProduct: simpleProducts.find(simpleProduct => simpleProduct.RecordId === product.RecordId),
                                    orderTemplate,
                                    customerInformation,
                                    unitConversions: unitConversions?.filter(x => x.ProductRecordId === product.RecordId),
                                    wishlists,
                                    orderTemplates
                                }}
                                productComparisonButton={productComparisonButton}
                                actionsAvailable={actionsAvailable}
                                cartViewProps={cartViewProps}
                                moduleId={moduleId}
                                moduleType={moduleType}
                                error={productErrors?.find(x => x.RecordId === product.RecordId)}
                                allowMultiSelect={allowMultiSelect}
                                min={min}
                                readOnly={readOnly}
                                alwaysBreakoff={alwaysBreakoff}
                                disableBuyButton={disableBuyButtons}
                                disableReorder={disableReorder}
                                disableQuantityInput={disableQuantityInput}
                                draggable={draggable}
                                historicalPrice={historicalPrice}
                                imageSettings={imageSettings}
                                showAvailability={showAvailability}
                                showPrices={showPrices}
                                showTotalPrices={showTotalPrices}
                                showCartCount={showCartCount}
                                buyAlwaysAdd={buyAlwaysAdd}
                                resetSelectedQuantities={!!product.RecordId && resetSelectedQuantities?.has(product.RecordId)}
                                smallerImage={smallerImage}
                                initialBreakoffEnabled={Boolean(product.RecordId && breakoffProducts?.[product.RecordId])}
                                userHasAccess={validatedProductAccess?.find(x => x.RecordId === product.RecordId)?.HasAccess}
                                onUpdateOrderTemplates={onUpdateOrderTemplates}
                                retrieveOrderTemplates={retrieveOrderTemplates}
                                onProductRemove={onProductRemove}
                                onUpdateQuantity={quantity => {
                                    onUpdateQuantity?.(product, quantity);
                                }}
                                onBreakoffChange={onBreakoffChange}
                                onDrop={handleDrop}
                                onDragOver={(recordId, overUnder) => {
                                    setDragOverProduct(recordId);
                                    setDragOverUnder(overUnder);
                                }}
                                onBuyValidate={onBuyValidate}
                            />
                        ));
                    }

                    return items;
                })}
            </ul>

            {isLoading && (
                <div className='product-search-loading-spinner-wrapper'>
                    <div className='product-search-loading-spinner'></div>
                </div>
            )}
        </div>
    );
};

const getInventoryLabel = (
    channelInventoryConfigurationId: number | undefined,
    attributeValues: AttributeValue[] | undefined,
    recordId: number,
    productInventoryStatus?: any
): string | undefined => {
    if (productInventoryStatus) {
        const shippingInventory = productInventoryStatus.find((shipping: any) => shipping.ProductAvailableQuantity.ProductId === recordId);
        if (shippingInventory && shippingInventory.StockLevelLabel) {
            return shippingInventory.StockLevelLabel;
        }
    }
    if (!channelInventoryConfigurationId || !ArrayExtensions.hasElements(attributeValues)) {
        return undefined;
    }
    const inventoryAttribute = attributeValues.find(attribute => attribute.RecordId === channelInventoryConfigurationId);
    if (inventoryAttribute) {
        return inventoryAttribute.TextValue;
    }
    return undefined;
};

export const productSearchResults2ProductList = (
    searchResults: ProductSearchResultExt[],
    channelInventoryConfigurationId: number | undefined,
    cart?: ICartState,
    productInventoryStatus?: IProductInventoryInformation[]
): ProductListProduct[] => {
    const productList: ProductListProduct[] = [];

    if (!searchResults) {
        return productList;
    }

    searchResults.map((searchResult, index) => {
        const cartLine = cart?.cart.CartLines?.find(x => x.ProductId === searchResult.RecordId);
        const productAvailability = productInventoryStatus?.find(x => x.ProductAvailableQuantity.ProductId === searchResult.RecordId);

        productList.push(
            new ProductListProduct(
                searchResult.RecordId,
                searchResult.ItemId,
                searchResult.Name,
                searchResult.PrimaryImageUrl,
                searchResult.DefaultUnitOfMeasure,
                searchResult.Price,
                getInventoryLabel(
                    channelInventoryConfigurationId,
                    searchResult.AttributeValues,
                    searchResult.RecordId,
                    productInventoryStatus
                ),
                cartLine?.Quantity,
                cartLine,
                undefined,
                undefined,
                productAvailability,
                searchResult.AlcoholPercentage,
                searchResult.VolumeCl,
                searchResult.PriceFailed,
                undefined,
                searchResult.CategoryPaths,
                searchResult.Badge,
                searchResult.ProductPrice,
                searchResult.Producer,
                searchResult.CountryAndDistrict,
                undefined,
                searchResult.Vintage,
                searchResult.SoldOutUntilDate,
                searchResult.SoldOutUnknownDate,
                undefined,
                undefined
            )
        );

        productList[productList.length - 1].BasePrice = searchResult.BasePrice;
    });

    return productList;
};

export const fullProducts2ProductList = (
    fullProducts: FullProduct[] | undefined,
    productAvailabilities: IProductInventoryInformation[] | undefined
): ProductListProduct[] => {
    const productList: ProductListProduct[] = [];

    if (!fullProducts) {
        return productList;
    }

    fullProducts.map((fullProduct, index) => {
        const productDetails = fullProduct.ProductDetails as SimpleProductExt;

        productList.push(
            new ProductListProduct(
                fullProduct.ProductDetails.RecordId,
                fullProduct.ProductDetails.ItemId,
                fullProduct.ProductDetails.Name,
                fullProduct.ProductDetails.PrimaryImageUrl,
                fullProduct.ProductDetails.DefaultUnitOfMeasure,
                fullProduct.ProductDetails.Price,
                productAvailabilities?.find(
                    avail => avail.ProductAvailableQuantity.ProductId === fullProduct.ProductDetails.RecordId
                )?.StockLevelLabel,
                undefined,
                undefined,
                undefined,
                undefined,
                productAvailabilities?.find(avail => avail.ProductAvailableQuantity.ProductId === fullProduct.ProductDetails.RecordId),
                productDetails.AlcoholPercentage,
                productDetails.VolumeCl,
                undefined,
                undefined,
                productDetails.CategoryPaths,
                productDetails.Badge,
                productDetails.ProductPrice,
                productDetails.Producer,
                productDetails.CountryAndDistrict,
                productDetails.Category,
                productDetails.Vintage,
                productDetails.SoldOutUntilDate,
                productDetails.SoldOutUnknownDate,
                undefined,
                undefined
            )
        );
    });

    return productList;
};

export const cartLines2ProductList = (
    cartLines: ICartlinesViewProps[],
    productAvailabilities: IProductInventoryInformation[] | undefined,
    context: ICoreContext,
    activePrices: ProductPrice[] | undefined
): ProductListProduct[] => {
    const productList: ProductListProduct[] = [];

    if (!cartLines) {
        return productList;
    }

    // Group free items by product id so it can be added to the non-free item line
    const freeItems = cartLines.reduce((acc, cartLine) => {
        const freeItemDiscount = cartLine.data?.cartline.DiscountLines?.find(x => x.PeriodicDiscountTypeValue === PeriodicDiscountOfferType.FreeItem);
        if (freeItemDiscount) {
            const productId = cartLine.data?.cartline.ProductId || 0;
            if (!acc[productId]) {
                acc[productId] = [];
            }
            acc[productId].push(cartLine);
        }
        return acc;
    }, {} as { [key: number]: ICartlinesViewProps[] });

    cartLines.map((cartLine, index) => {
        // Don't create a separate line for free items, instead they'll be added to the "main" line for the product
        if (cartLine.data?.cartline.DiscountLines?.some(x => x.PeriodicDiscountTypeValue === PeriodicDiscountOfferType.FreeItem)) {
            return;
        }

        const product = cartLine.data?.product as SimpleProductExt | undefined;
        /* const price = cartLine.data?.cartline.Price || 0; */
        const price = cartLine.data?.cartline?.NetAmountWithoutTax ?
            cartLine.data?.cartline.NetAmountWithoutTax / (cartLine.data?.cartline?.Quantity || 1) :
            cartLine.data?.cartline.Price || 0;
        const fallImageUrl = getFallbackImageUrl(product?.ItemId, context.request.apiSettings);
        const productPrice = activePrices?.find(x => x.ProductId === product?.RecordId);
        const availability = productAvailabilities?.find(
            avail => avail.ProductAvailableQuantity.ProductId === product?.RecordId
        );

        const freeItemLines = freeItems[product?.RecordId || 0] || [];
        const freeQuantity = freeItemLines.reduce((acc, line) => acc + (line.data?.cartline.Quantity || 0), 0);

        productList.push(
            new ProductListProduct(
                product?.RecordId,
                product?.ItemId,
                product?.Name,
                product?.PrimaryImageUrl || fallImageUrl,
                product?.DefaultUnitOfMeasure,
                price,
                availability?.StockLevelLabel,
                cartLine.data?.cartline.Quantity,
                cartLine.data?.cartline,
                cartLine.data?.cartline.DiscountAmount,
                cartLine.data?.cartline.NetAmountWithoutTax,
                availability,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                getDiscountBadge(productPrice),
                productPrice,
                product?.Producer,
                product?.CountryAndDistrict,
                product?.Category,
                product?.Vintage,
                product?.SoldOutUntilDate,
                product?.SoldOutUnknownDate,
                undefined,
                freeQuantity
            )
        );
    });

    return productList;
};

export const orderTemplateLines2ProductList = (
    lines: CountingListLine[],
    productAvailabilities: IProductInventoryInformation[] | undefined
): ProductListProduct[] => {
    const productList: ProductListProduct[] = [];

    if (!lines) {
        return productList;
    }

    lines.map((line, index) => {
        const productDetails = line.fullProduct?.ProductDetails as SimpleProductExt;
        const availability = productAvailabilities?.find(
            avail => avail.ProductAvailableQuantity.ProductId === line.fullProduct?.ProductDetails.RecordId
        );

        const displayOrder = line.productListLine.ExtensionProperties?.find(prop => prop.Key === 'DISPLAYORDER')?.Value?.DecimalValue;

        const product = new ProductListProduct(
            line.fullProduct?.ProductDetails.RecordId,
            line.fullProduct?.ProductDetails.ItemId,
            line.fullProduct?.ProductDetails.Name,
            line.fullProduct?.ProductDetails.PrimaryImageUrl,
            line.productListLine.UnitOfMeasure,
            line.fullProduct?.ProductDetails.Price,
            availability?.StockLevelLabel,
            undefined,
            undefined,
            undefined,
            undefined,
            availability,
            productDetails.Brand,
            productDetails.Manufacturer,
            undefined,
            displayOrder,
            productDetails.CategoryPaths,
            productDetails.Badge,
            productDetails.ProductPrice,
            productDetails.Producer,
            productDetails.CountryAndDistrict,
            productDetails.Category,
            productDetails.Vintage,
            productDetails.SoldOutUntilDate,
            productDetails.SoldOutUnknownDate,
            line.productListLine.LineId
        );

        product.CurrentQuantity = line.productListLine.Quantity;
        product.LineCreatedDateTime = line.productListLine.CreatedDateTime || line.productListLine.LastUpdated;

        if (typeof product.LineCreatedDateTime === 'string') {
            product.LineCreatedDateTime = new Date(product.LineCreatedDateTime);
        }

        product.BasePrice = line.fullProduct?.ProductDetails.BasePrice;
        product.PriceFailed = (line as any).PriceFailed;

        productList.push(product);
    });

    return productList;
};
